
import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiDollarSign, FiUpload, FiCreditCard, FiLoader } from 'react-icons/fi';
import {API_ROUTES} from "../config/apiRoutes";


const ReceiptModal = ({ isOpen, order, onClose, onSubmit, isSubmitting }) => {
    const [receiptData, setReceiptData] = useState({
        invoice_id: '',
        account_id: '',
        amount: '',
        description: '',
        image: null,
        imagePreview: null
    });

    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    // دریافت لیست حساب‌ها از API
    useEffect(() => {
        if (isOpen && order) {
            fetchAccounts();
            setReceiptData({
                invoice_id: order?.invoice_id?.toString() || order?.id?.toString() || '',
                account_id: '',
                amount: order?.remaining_amount?.toString() || order?.price?.toString() || '',
                description: '',
                image: null,
                imagePreview: null
            });
        }
    }, [isOpen, order]);

    const fetchAccounts = async () => {
        setLoadingAccounts(true);
        setAccountsError(null);
        try {
            const response = await API_ROUTES.BANKS_ACCOUNT();

            if (response.data) {
                setAccounts(response.data.data || []);
            } else {
                setAccountsError(response.data.message || 'خطا در دریافت حساب‌ها');
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setAccountsError(error.response?.data?.message || 'خطا در ارتباط با سرور');
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setReceiptData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('لطفاً فقط فایل تصویری انتخاب کنید');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('حجم فایل باید کمتر از 5 مگابایت باشد');
                return;
            }
            setReceiptData(prev => ({
                ...prev,
                image: file,
                imagePreview: URL.createObjectURL(file)
            }));
        }
    };

    const removeImage = () => {
        setReceiptData(prev => ({
            ...prev,
            image: null,
            imagePreview: null
        }));
    };

    const handleSubmit = () => {
        // اعتبارسنجی
        if (!receiptData.account_id) {
            alert('لطفاً شماره حساب را انتخاب کنید');
            return;
        }
        if (!receiptData.amount) {
            alert('لطفاً مبلغ را وارد کنید');
            return;
        }
        if (parseFloat(receiptData.amount) <= 0) {
            alert('مبلغ باید بزرگتر از صفر باشد');
            return;
        }

        // ساخت FormData برای ارسال به سرور
        const formData = new FormData();
        formData.append('invoice_id', receiptData.invoice_id);
        formData.append('account_id', receiptData.account_id);
        formData.append('amount', receiptData.amount);
        formData.append('description', receiptData.description);
        if (receiptData.image) {
            formData.append('image', receiptData.image);
        }

        onSubmit(formData);
    };

    const formatPrice = (price) => {
        if (!price && price !== 0) return '0';
        return new Intl.NumberFormat('fa-IR').format(price);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-white z-50 flex flex-col animate-fadeIn">
            {/* هدر مودال */}
            <div className="flex-shrink-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white p-5 shadow-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <FiCheck className="text-white text-xl" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">ثبت دریافتی</h2>
                            <p className="text-green-100 text-sm">مشخصات دریافت را وارد کنید</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors"
                    >
                        <FiX className="text-white text-xl" />
                    </button>
                </div>
            </div>

            {/* محتوای مودال - اسکرول‌دار */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                <div className="max-w-2xl mx-auto space-y-6">
                    {/* اطلاعات سفارش */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                            اطلاعات فاکتور
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">شماره سفارش</p>
                                <p className="font-bold text-gray-800 text-lg">#{order?.id}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">نام مشتری</p>
                                <p className="font-medium text-gray-800">{order?.customer_name || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">مبلغ فاکتور</p>
                                <p className="font-semibold text-green-600 text-lg">{formatPrice(order?.price)} <span className="text-sm">ریال</span></p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">مبلغ باقیمانده</p>
                                <p className="font-semibold text-amber-600 text-lg">{formatPrice(order?.remaining_amount)} <span className="text-sm">ریال</span></p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">کد واحد</p>
                                <p className="font-mono text-gray-800">{order?.unit_id || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">شناسه فاکتور</p>
                                <p className="font-mono text-gray-800">{order?.invoice_id || '-'}</p>
                            </div>
                        </div>
                    </div>

                    {/* فرم ثبت دریافتی */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                            اطلاعات دریافت
                        </h3>

                        <div className="space-y-5">
                            {/* انتخاب شماره حساب */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    شماره حساب مقصد <span className="text-red-500">*</span>
                                </label>

                                {loadingAccounts ? (
                                    <div className="flex items-center justify-center p-4 border border-gray-200 rounded-xl bg-gray-50">
                                        <FiLoader className="animate-spin text-green-600 text-xl" />
                                        <span className="mr-2 text-gray-500">در حال بارگذاری حساب‌ها...</span>
                                    </div>
                                ) : accountsError ? (
                                    <div className="p-4 border border-red-200 rounded-xl bg-red-50 text-red-600 text-sm text-center">
                                        {accountsError}
                                        <button
                                            onClick={fetchAccounts}
                                            className="block mx-auto mt-2 text-green-600 hover:text-green-700"
                                        >
                                            تلاش مجدد
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                            <FiCreditCard className="text-gray-400" size={20} />
                                        </div>
                                        <select
                                            name="account_id"
                                            value={receiptData.account_id}
                                            onChange={handleChange}
                                            className="w-full pr-12 pl-4 py-3 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
                                        >
                                            <option value="">انتخاب شماره حساب...</option>
                                            {accounts.map((account) => (
                                                <option key={account.id} value={account.id}>
                                                    {account.bank_name || account.name} - {account.account_number}
                                                    {account.is_default && ' (پیش‌فرض)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {!loadingAccounts && !accountsError && accounts.length === 0 && (
                                    <div className="p-4 border border-amber-200 rounded-xl bg-amber-50 text-amber-700 text-sm text-center">
                                        <p>هیچ حسابی ثبت نشده است.</p>
                                        <p className="text-xs mt-1">لطفاً ابتدا حساب بانکی خود را ثبت کنید.</p>
                                    </div>
                                )}
                            </div>

                            {/* مبلغ دریافتی */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    مبلغ دریافتی <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                        <FiDollarSign className="text-gray-400" size={20} />
                                    </div>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={receiptData.amount}
                                        onChange={handleChange}
                                        placeholder="0"
                                        className="w-full pr-12 pl-4 py-3 text-lg border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        autoFocus
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1 flex justify-between">
                                    <span>مبلغ فاکتور: {formatPrice(order?.price)} ریال</span>
                                    <span className="text-amber-600">مبلغ باقیمانده: {formatPrice(order?.remaining_amount)} ریال</span>
                                </p>
                            </div>

                            {/* توضیحات */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    توضیحات
                                    <span className="text-xs text-gray-400 mr-1">(اختیاری)</span>
                                </label>
                                <textarea
                                    name="description"
                                    value={receiptData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="توضیحات خود را وارد کنید..."
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                                />
                            </div>

                            {/* آپلود عکس */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    آپلود عکس رسید
                                    <span className="text-xs text-gray-400 mr-1">(اختیاری)</span>
                                </label>
                                <div
                                    className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-400 transition-colors cursor-pointer bg-gray-50"
                                    onClick={() => document.getElementById('imageInput').click()}
                                >
                                    <input
                                        id="imageInput"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                    />
                                    {receiptData.imagePreview ? (
                                        <div className="relative inline-block">
                                            <img
                                                src={receiptData.imagePreview}
                                                alt="پیش‌نمایش رسید"
                                                className="max-h-48 rounded-lg shadow-md"
                                            />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeImage();
                                                }}
                                                className="absolute -top-2 -left-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                                            >
                                                <FiX size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <FiUpload className="mx-auto text-gray-400 text-4xl mb-3" />
                                            <p className="text-gray-600 font-medium">برای آپلود عکس کلیک کنید</p>
                                            <p className="text-xs text-gray-400 mt-2">فرمت‌های مجاز: JPG, PNG, GIF (حداکثر 5MB)</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* دکمه‌های مودال - ثابت در پایین */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200 p-5 shadow-lg">
                <div className="max-w-2xl mx-auto flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium text-lg disabled:opacity-50"
                    >
                        انصراف
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !receiptData.account_id || !receiptData.amount}
                        className="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-medium text-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                <span>در حال ثبت...</span>
                            </>
                        ) : (
                            <>
                                <FiCheck size={20} />
                                <span>ثبت دریافتی</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReceiptModal;